import { useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import useEditFormStore from "../../../../../../state-management/useEditFormStore";
import {
	useDeleteCompanyTaxRelief,
	useGetCompanyTaxRelief,
} from "../../../../../../services/company-tax-relief.service";
import ActionPopover from "../../../../../../components/popovers/actionPopover";
import DeleteModal from "../../../../../../components/modals/DeleteModal";
import { TaxReliefDto } from "../../../../../../models/tax-relief.dto";
import TableFilter from "../../../../../../components/table/filter/TableFilter";
import { APP_CONFIG } from "../../../../../../helpers/appHelpers";
import { INCOME_PERIOD } from "../../../../../../models/tax-rate.dto";
import { useGetTaxReliefType } from "../../../../../../services/tax-relief-type.service";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../../../helpers/useManageTable";
import { reliefTaxRuleColumns } from "../../../../../admin/system_configurations/views/tax_rules/tanstack-columns";
import TanStackEmployeeTable from "../../../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../../../models/table-configuration.dto";
import CompanyReliefTaxRuleModalForm from "../modals/ReliefTaxRuleModalForm";

interface Props {
	companyId: number;
}

const CompanyReliefTaxRuleView = ({ companyId }: Props) => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);

	const { tableAPIRef, tableData, isLoading, pageCount } =
		useManageTableWithSearchParams<TaxReliefDto>({
			fetchHook: (params) => useGetCompanyTaxRelief(companyId, params),
		});

	const { data: taxReliefTypes } = useGetTaxReliefType({
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: "name:asc" as any,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteTaxRelief } = useDeleteCompanyTaxRelief();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);
		deleteTaxRelief(
			{ companyId, id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	return (
		<>
			<div className="flex justify-between items-center mt-7 mb-8">
				<TableFilter
					fields={[
						{
							title: "",
							fieldName: "",
							fieldType: "SORT",
							options: [
								{ title: "Date Created", fieldName: "createdAt" },
								{ title: "Date Modified", fieldName: "modifiedAt" },
							],
						},
						{
							title: "Tax Relief Type",
							fieldName: "taxReliefTypeId",
							fieldType: "SELECT_FIELD",
							options: taxReliefTypes?.data || [],
						},
						{
							title: "Income Period",
							fieldName: "incomePeriod",
							fieldType: "CHECKBOX_LIST",
							options: INCOME_PERIOD,
						},
					]}
				/>
				<div
					className="flex items-center text-primary-blue text-sm cursor-pointer"
					onClick={handleOpenModal}
				>
					<AiOutlinePlus className="mr-1 text-sm" />
					Add New
				</div>
			</div>
			<TanStackEmployeeTable
				tableId={TableId.RELIEF_TAX_RULE}
				data={tableData}
				columns={reliefTaxRuleColumns}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={{}}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<CompanyReliefTaxRuleModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={isEditForm ? "Update Relief Rule" : "Add New Relief Rule"}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					companyId={companyId}
					reloadTableData={reloadTableData}
				/>
			)}
		</>
	);
};

export default CompanyReliefTaxRuleView;
